import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { isDevMode } from '../utils/test-modes';
import authStore from '../utils/auth-store';
import { getVersionContent } from '@api/revisions';
import { IDataModel, IResponseModel, IResponseStatus } from 'types/common';
import { DraftContentType } from './types/draftTypes';

const BASE_API_URL = 'cms/version';
// noteIdorAlias === entitySlug
const getUrl = (
  type: 'all' | 'content' | 'restore' | 'delete',
  entitySlug: string
) => {
  const entityType = 'article';
  const entityAttribute = 'content';
  const baseUrl = `${BASE_API_URL}/${entityType}/${entitySlug}/${entityAttribute}`;
  switch (type) {
    case 'all':
    case 'content':
      return baseUrl;
    case 'restore':
      return `${baseUrl}/restore`;
    case 'delete':
      return `${baseUrl}/delete`;
    default:
      return '';
  }
};

export const versionHistoryApi = createApi({
  reducerPath: 'versionHistoryApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${
      isDevMode ? process.env.NEXT_PUBLIC_STAGING_URL : ''
    }topics/api/v1/`,
    prepareHeaders: (headers) => {
      const jwt = authStore.get().jwt;
      if (jwt) {
        headers.set('X-User-Token', jwt);
      }
      return headers;
    }
  }),
  tagTypes: [
  'GetVersionsList',
  'GetVersionContent',
  'GetArticleContent',
  'GetDraftContent',
],
  endpoints: (builder) => ({
    fetchVersionsList: builder.query({
      // fetch version list
      query: (args: { entitySlug: string }) => ({
        url: getUrl('all', args.entitySlug)
      }),
      transformResponse: (response : {
        total_count: number;
        data: any;
      }) => ({
        versionsCount: response.total_count,
        versions: response.data
      }),
      providesTags: ['GetVersionsList']
    }),
    fetchVersionContent: builder.query({
      // fetch version content by version id
      queryFn: (args: { versionID: string[]; entitySlug: string }) => {
        const versionsContentPromise = args.versionID.map((id) =>
          getVersionContent(id, args.entitySlug)
        );
        return Promise.all(versionsContentPromise).then((res) => ({
          data: res
        }));
      },
      providesTags: ['GetVersionContent']
    }),
    fetchArticleContent: builder.query({
      // fetch note
      query: (args: { noteIdOrAlias: string }) => ({
        url: `cms/hedgedoc/${args.noteIdOrAlias}/content`
      }),
      providesTags: ['GetArticleContent']
    }),
    updateArticleContent: builder.mutation({
      // to save article
      query: (args: { noteIdOrAlias: string; content: string }) => ({
        url: `cms/hedgedoc/${args.noteIdOrAlias}/content`,
        method: 'PATCH',
        body: {
          content: args.content
        }
      }),
      invalidatesTags: ['GetVersionsList', 'GetArticleContent']
    }),
    restoreArticleContent: builder.mutation({
      query: (args: { versionId: string; entitySlug: string }) => ({
        url: getUrl('restore', args.entitySlug),
        method: 'POST',
        body: {
          version_id: args.versionId
        }
      }),
      invalidatesTags: ['GetArticleContent', 'GetVersionsList']
    }),
    deleteArticleContent: builder.mutation({
      query: (args: { versionId: string; entitySlug: string }) => ({
        url: getUrl('delete', args.entitySlug),
        method: 'POST',
        body: {
          version_id: args.versionId
        }
      }),
      invalidatesTags: ['GetVersionsList']
    }),
    fetchDraftContent: builder.query<DraftContentType, {
      noteIdOrAlias: string;
    }>({
      // fetch draft content
      query: (args) => ({
        url: `cms/hedgedoc/${args.noteIdOrAlias}/draft_content`,
      }),
      transformResponse: (args: IResponseModel<[IDataModel<DraftContentType>]>) => {
        const { attributes } = args.data?.[0];
        return (attributes);
      },
      providesTags: ['GetDraftContent'],
    }),
    updateDraftContent: builder.mutation<IResponseStatus, {
      noteIdOrAlias: string; content: string;
    }>({
      // update draft content
      query: (args) => ({
        url: `cms/hedgedoc/${args.noteIdOrAlias}/draft_content`,
        method: 'PATCH',
        body: {
          content: args.content,
        }
      }),
      invalidatesTags: (_, responseStatus) => (responseStatus?.status === 404 ? [] : ['GetDraftContent'])
    }),
    updateDraftStatus: builder.mutation<IResponseStatus, {
      noteIdOrAlias: string; draftStatus: 'rejected' | 'approved';
    }>({
      // update draft content
      query: (args) => ({
        url: `cms/hedgedoc/${args.noteIdOrAlias}/draft_status`,
        method: 'PATCH',
        body: {
          draft_status: args.draftStatus,
        }
      }),
      invalidatesTags: (_, responseStatus) => (responseStatus?.status === 404 ? [] : ['GetDraftContent', 'GetArticleContent', 'GetVersionsList'])
    }),
  })
});

export const {
  useFetchVersionsListQuery,
  useFetchVersionContentQuery,
  useLazyFetchVersionContentQuery,
  useLazyFetchVersionsListQuery,
  useLazyFetchArticleContentQuery,
  useFetchArticleContentQuery,
  useUpdateArticleContentMutation,
  useRestoreArticleContentMutation,
  useDeleteArticleContentMutation,
  useFetchDraftContentQuery,
  useLazyFetchDraftContentQuery,
  useUpdateDraftContentMutation,
  useUpdateDraftStatusMutation,
} = versionHistoryApi;

export type VersionHistoryReducer = typeof versionHistoryApi.reducer;
